import { z } from 'zod';
import { paginationSchema, sortOrderSchema } from './helpers';
import { knownTotalParamsSchema, knownTotalQuerySchema } from './known-total';
import { severitySchema } from './shared/severity';
import { aiInstructionUserBasicSchema } from './ai-instructions';
import { ColumnDataTypes, FiltersSchema, TableResponseMetaSchema, createTableQuerySchema, } from './table';
/**
 * Inventory → Skills (KIR-4699).
 *
 * Identity is `(name, source)`, not the content id AI Instructions groups on:
 * the same SKILL.md in a project and in `~/.claude/skills` is two skills,
 * marked independently — hence its own table.
 */
/** No Allow list exists, so a row is one or the other. */
const SKILL_LIST_STATES = ['allowed', 'marked_for_delete'];
/**
 * A dedicated query param rather than a column filter, like the Packages
 * page: the state is computed from the rule list, not stored on the row.
 */
const SKILL_LIST_STATE_FILTERS = [
    'all',
    'allowed',
    'marked_for_delete',
];
export const skillListStateFilterSchema = z.enum(SKILL_LIST_STATE_FILTERS);
const skillListStateSchema = z.enum(SKILL_LIST_STATES);
/**
 * One project a skill was seen in.
 *
 * An object rather than a bare name (KIR-4850 review): project names are
 * repo/folder basenames, so `github.com/acme/api` and `github.com/globex/api`
 * are both "api". Keying the cell count and the drilldown on the name made a
 * skill in two such repos render "1" while the Projects sort — which counts
 * distinct project ids — ordered it as 2.
 *
 * `slug` is the stable per-organization key (and the value the Projects
 * filter sends back); `origin` is the normalized git origin, used to tell
 * two same-named projects apart and null for a repo-less folder.
 */
export const skillProjectRefSchema = z.object({
    slug: z.string(),
    name: z.string(),
    origin: z.string().nullable(),
});
export const skillDataTableItemSchema = z.object({
    /** Folded skill name — the first half of the row's identity. */
    name: z.string(),
    /**
     * The skill directory as the author cased it, for the Name column. `name`
     * is folded so the row, the rule, and enforcement agree on one key; showing
     * it verbatim would render `PDF-Processing` as `pdf-processing`. Falls back
     * to `name` when the path cannot confirm a casing. Display only — never
     * send this back as a rule.
     */
    displayName: z.string(),
    /** `project`, `user`, or `plugin:<plugin-name>` — the second half. */
    source: z.string(),
    /**
     * The third half: which project a `project`-source skill belongs to, as the
     * normalized git origin. Null for `user` / `plugin:` skills and for project
     * skills whose client reports no origin. Send it back verbatim in the rule
     * object — two repos' same-named skills are two rows, marked separately.
     */
    project: z.string().nullable(),
    /** Representative path, shown under the name. */
    filePath: z.string(),
    filePaths: z.array(z.string()),
    /** The manifest's content, for the expanded row's file preview. */
    contentId: z.string().uuid().nullable(),
    summary: z.string().nullable(),
    users: z.array(aiInstructionUserBasicSchema),
    projects: z.array(skillProjectRefSchema),
    /** AgentMesh risk; null until Phase 2, where the column shows a placeholder. */
    riskLevel: severitySchema.nullable(),
    listState: skillListStateSchema,
    firstSeenAt: z.string(),
    lastSeenAt: z.string(),
});
const SKILL_COLUMN_DATA_TYPES = {
    name: ColumnDataTypes.STRING,
    source: ColumnDataTypes.STRING,
    file: ColumnDataTypes.STRING,
    projects: ColumnDataTypes.ENUM,
    users: ColumnDataTypes.ENUM,
};
/** Fields the Skills filter-options endpoint can enumerate. */
export const SKILL_FILTER_OPTION_FIELDS = {
    NAME: 'name',
    FILE: 'file',
    PROJECTS: 'projects',
    USERS: 'users',
};
export const skillFilterOptionFieldSchema = z.enum([
    SKILL_FILTER_OPTION_FIELDS.NAME,
    SKILL_FILTER_OPTION_FIELDS.FILE,
    SKILL_FILTER_OPTION_FIELDS.PROJECTS,
    SKILL_FILTER_OPTION_FIELDS.USERS,
]);
export const skillFilterOptionsQuerySchema = z.object({
    field: skillFilterOptionFieldSchema,
    /**
     * Bounded because it reaches Postgres as an `ILIKE` pattern: an unbounded
     * string lets a caller push an arbitrarily large pattern through every
     * candidate row. 200 is far past any real project or skill name.
     */
    search: z.string().max(200).optional(),
    limit: z.coerce.number().int().min(1).max(100).optional(),
    offset: z.coerce.number().int().min(0).optional(),
    /**
     * The table's pill. Without it a dropdown offers values that exist only in
     * the other state, and picking one empties the current view.
     */
    listState: skillListStateFilterSchema.optional(),
});
/**
 * An option with its display text.
 *
 * `value` is what the filter sends back and is deliberately NOT the label:
 * projects key on their slug and users on their id, because two projects can
 * share a display name and a user may have no name at all.
 */
export const skillFilterOptionSchema = z.object({
    value: z.string(),
    label: z.string(),
    name: z.string().nullable(),
    email: z.string().nullable(),
});
export const skillFilterOptionsResponseSchema = z.object({
    field: skillFilterOptionFieldSchema,
    options: z.array(z.string()),
    optionsWithMetadata: z.array(skillFilterOptionSchema).optional(),
    pagination: z.object({
        total: z.number().int().min(0),
        limit: z.number().int().min(1),
        offset: z.number().int().min(0),
        hasMore: z.boolean(),
        nextOffset: z.number().int().min(0).nullable().optional(),
    }),
});
export const skillDataTableSortFieldSchema = z.enum([
    'name',
    'source',
    'file',
    'projects',
    'users',
    'listState',
]);
const skillDataTableQueryBaseSchema = paginationSchema.extend({
    sortBy: skillDataTableSortFieldSchema.optional(),
    sortOrder: sortOrderSchema.optional(),
    search: z.string().optional(),
    /** Pill filter; omitted or `all` applies no state narrowing. */
    listState: skillListStateFilterSchema.optional(),
    knownTotal: knownTotalQuerySchema,
});
export const skillDataTableQuerySchema = createTableQuerySchema(skillDataTableQueryBaseSchema, SKILL_COLUMN_DATA_TYPES);
/**
 * What a caller hands {@link listSkillsDataTable}, as opposed to
 * {@link skillDataTableQuerySchema}, which coerces the same fields off a
 * querystring. Everything is optional: the server supplies every default.
 */
export const listSkillsDataTableParamsSchema = z.object({
    page: z.number().int().min(1).optional(),
    limit: z.number().int().min(1).max(100).optional(),
    sortBy: skillDataTableSortFieldSchema.optional(),
    sortOrder: sortOrderSchema.optional(),
    search: z.string().optional(),
    listState: skillListStateFilterSchema.optional(),
    filters: z.array(FiltersSchema.element).optional(),
    knownTotal: knownTotalParamsSchema,
});
export const skillTableResponseSchema = z.object({
    data: z.array(skillDataTableItemSchema),
    pagination: z.object({
        page: z.number().int().min(1),
        limit: z.number().int().min(1),
        total: z.number().int().min(0),
        hasMore: z.boolean(),
    }),
    meta: TableResponseMetaSchema,
});
//# sourceMappingURL=skills.js.map